import type { OperatingLoad } from "./types";
import {
  resolveSuitesForLoad,
  type SuiteEventType,
  type SuiteLayerRef,
  type SuiteTool,
  type ValidatorResult,
} from "./suite-registry";

export type CompositionDecision = "allow" | "approval_required" | "block";

export interface CompositionResolution {
  decision: CompositionDecision;
  results: ValidatorResult[];
  blocks: ValidatorResult[];
  warnings: ValidatorResult[];
  overridesApplied: string[];
  evidencePayload: {
    layer_contributions: Array<{
      layer: string;
      validator: string;
      result: ValidatorResult["status"];
      reason: string;
    }>;
    final_decision: CompositionDecision;
    overrides_applied: string[];
  };
}

export async function runValidators(load: OperatingLoad): Promise<ValidatorResult[]> {
  const results: ValidatorResult[] = [];
  const suites = resolveSuitesForLoad(load);

  for (const suite of suites) {
    for (const validator of suite.validators) {
      try {
        results.push(await validator.evaluate(load));
      } catch (error) {
        results.push(validatorFailureResult(suite.id, suite.layer, validator.name, error));
      }
    }
  }

  return results;
}

export function resolveConflicts(results: ValidatorResult[]): CompositionResolution {
  const overrideTargets = new Set(
    results.flatMap((result) =>
      result.status === "override" ? (result.overrides ?? []).map(overrideKey) : [],
    ),
  );
  const blocks = results.filter(
    (result) => result.status === "block" && !overrideTargets.has(resultKey(result)),
  );
  const warnings = results.filter((result) => result.status === "warn");
  const overridesApplied = [...overrideTargets];
  const decision: CompositionDecision =
    blocks.length > 0 ? "block" : warnings.length > 0 ? "approval_required" : "allow";

  return {
    decision,
    results,
    blocks,
    warnings,
    overridesApplied,
    evidencePayload: {
      layer_contributions: results.map((result) => ({
        layer: layerLabel(result.layer),
        validator: result.validator,
        result: result.status,
        reason: result.reason,
      })),
      final_decision: decision,
      overrides_applied: overridesApplied,
    },
  };
}

export async function composeLoadValidation(load: OperatingLoad): Promise<CompositionResolution> {
  return resolveConflicts(await runValidators(load));
}

export function resolveTools(eventType: SuiteEventType, load: OperatingLoad): SuiteTool[] {
  const applicable = resolveSuitesForLoad(load)
    .flatMap((suite) => suite.tools)
    .filter((tool) => tool.eventTypes.includes(eventType));

  return applicable
    .filter((tool) => !isSuperseded(tool, applicable))
    .sort((left, right) => right.specificity - left.specificity || left.name.localeCompare(right.name));
}

export function buildCompositionDecisionEvidence(args: {
  tenantId: string;
  loadId: string;
  resolution: CompositionResolution;
}) {
  const now = new Date().toISOString();
  return {
    tenantId: args.tenantId,
    loadId: args.loadId,
    type: "composition_decision" as const,
    source: "suite-composition-engine",
    occurredAt: now,
    receivedAt: now,
    reliabilityScore: 1,
    payload: args.resolution.evidencePayload,
    createdAt: Date.now(),
  };
}

function isSuperseded(tool: SuiteTool, applicable: SuiteTool[]): boolean {
  return applicable.some(
    (candidate) =>
      candidate !== tool &&
      candidate.specificity > tool.specificity &&
      candidate.supersedes.includes(tool.name),
  );
}

function validatorFailureResult(
  suiteId: string,
  layer: SuiteLayerRef,
  validator: string,
  error: unknown,
): ValidatorResult {
  const reason = `validator unavailable: ${error instanceof Error ? error.message : String(error)}`;
  if (layer.kind === "universal" || layer.kind === "regulatory") {
    return {
      id: `${suiteId}:${validator}:failure`,
      suiteId,
      validator,
      layer,
      status: "block",
      reason,
    };
  }
  if (layer.kind === "customer_sop") {
    return {
      id: `${suiteId}:${validator}:failure`,
      suiteId,
      validator,
      layer,
      status: "inform",
      reason,
    };
  }
  return {
    id: `${suiteId}:${validator}:failure`,
    suiteId,
    validator,
    layer,
    status: "warn",
    reason,
  };
}

function layerLabel(layer: SuiteLayerRef): string {
  return layer.kind === "universal" ? "core" : `${layer.kind}.${layer.key}`;
}

function resultKey(result: ValidatorResult): string {
  return `${result.suiteId}:${result.validator}`;
}

function overrideKey(target: { suiteId: string; validatorName: string }): string {
  return `${target.suiteId}:${target.validatorName}`;
}
